import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  RefreshCcw,
  User,
  FileText,
  Eye,
  Download,
  Phone,
  CheckCircle2,
  ShoppingBag,
  Truck,
X,
Search,
} from "lucide-react";
import { env } from "../../../config/env";
import { DeliveryPartnersService } from "../../../services/deliveryPartners.service";

const PINK = "#d86a78";
const PINK_HOVER = "#c95b69";
const BORDER = "#f2c6cc";

function resolveAssetUrl(url) {
  const value = String(url || "").trim();
  if (!value) return "";
  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:") ||
    value.startsWith("data:")
  ) {
    return value;
  }
  return `${env.AUTH_BASE_URL}${value.startsWith("/") ? "" : "/"}${value}`;
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[20px] bg-white ${className}`}
      style={{ border: `1px solid ${BORDER}` }}
    >
      {children}
    </div>
  );
}

function Badge({ children, type = "default" }) {
  const styles = {
    active: { bg: "#eef9ee", color: "#69a65f" },
    inactive: { bg: "#fff1f1", color: "#d86a78" },
    approved: { bg: "#eef9ee", color: "#69a65f" },
    pending: { bg: "#fff8e7", color: "#b7791f" },
    rejected: { bg: "#fff1f1", color: "#d86a78" },
    default: { bg: "#f4f6f8", color: "#64748b" },
  };

  const s = styles[type] || styles.default;

  return (
    <span
      className="inline-flex min-h-[30px] items-center rounded-full px-3 text-[12px] font-semibold"
      style={{ background: s.bg, color: s.color }}
    >
      {children}
    </span>
  );
}

function Field({ label, value }) {
  return (
    <div
      className="rounded-[14px] bg-[#fffdfd] p-4"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 break-words text-[14px] font-medium text-slate-700">
        {value || "-"}
      </div>
    </div>
  );
}

function InputField({ label, name, value, onChange, placeholder = "" }) {
  return (
    <label className="block">
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-[46px] w-full rounded-[12px] bg-white px-4 text-[14px] text-slate-700 outline-none"
        style={{ border: `1px solid ${BORDER}` }}
      />
    </label>
  );
}

function getDocUrl(doc) {
  if (!doc) return "";
  if (typeof doc === "string") return resolveAssetUrl(doc);
  return resolveAssetUrl(doc.url || doc.path || doc.key || "");
}

function getDocName(doc, fallback) {
  if (!doc) return fallback || "Document";
  if (typeof doc === "string") return fallback || "Document";
  return doc.originalName || doc.fileName || fallback || "Document";
}

async function forceDownload(url, filename = "download") {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error("Download failed");
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    alert(err?.message || "Download failed");
  }
}

function DocumentCard({ title, doc }) {
  const url = getDocUrl(doc);
  const name = getDocName(doc, title);

  if (!doc || !url) return null;

  return (
    <div
      className="rounded-[16px] p-4"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px]"
            style={{ background: "rgba(216,106,120,0.10)" }}
          >
            <FileText size={18} color={PINK} />
          </div>

          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-slate-700">
              {title}
            </div>
            <div className="mt-1 break-all text-[12px] text-slate-400">
              {name}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="rounded-[10px] p-2 text-slate-500 transition hover:bg-slate-50 hover:text-[#d86a78]"
            title="View"
          >
            <Eye size={16} />
          </a>

          <button
            type="button"
            onClick={() => forceDownload(url, name)}
            className="rounded-[10px] p-2 text-slate-500 transition hover:bg-slate-50 hover:text-[#d86a78]"
            title="Download"
          >
            <Download size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeStatus(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function OrderStatCard({ label, value, hint }) {
  return (
    <div
      className="rounded-[16px] bg-[#fffdfd] p-4"
      style={{ border: `1px solid ${BORDER}` }}
    >
      <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-2 text-[24px] font-bold text-slate-800">{value || 0}</div>
      {hint ? <div className="mt-1 text-[12px] text-slate-400">{hint}</div> : null}
    </div>
  );
}

function DeliveryOrderInfoCard({ partner }) {
  const summary = partner?.orderSummary || {};
  const orders = Array.isArray(partner?.orders) ? partner.orders : [];
  const recentOrders = orders.slice(0, 5);

  const [showAllOrders, setShowAllOrders] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return orders;

    return orders.filter((order) =>
      [
        order.orderNumber,
        order.orderId,
        order.taskStatus,
        order.assignmentStatus,
        order.dropName,
        order.dropPhone,
        order.customerSnapshot?.name,
        order.customerSnapshot?.phone,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [orders, orderSearch]);

  const OrdersTable = ({ rows }) => (
    <div className="max-h-[520px] overflow-auto">
      <table className="w-full min-w-[900px]">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-[#f8dde1] text-left text-[13px] font-semibold text-slate-600">
            <th className="px-4 py-3">Order</th>
            <th className="px-4 py-3">Task Status</th>
            <th className="px-4 py-3">Assignment</th>
            <th className="px-4 py-3">Customer</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Delivered</th>
          </tr>
        </thead>

        <tbody>
          {rows.length ? (
            rows.map((order) => (
              <tr
                key={order.id || order.orderId || order.orderNumber}
                className="border-t border-[#f8dde1] text-[14px] text-slate-700"
              >
                <td className="px-4 py-3 font-semibold text-slate-800">
                  {order.orderNumber || order.orderId || "-"}
                </td>
                <td className="px-4 py-3">{normalizeStatus(order.taskStatus)}</td>
                <td className="px-4 py-3">{normalizeStatus(order.assignmentStatus)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">
                    {order.dropName || order.customerSnapshot?.name || "-"}
                  </div>
                  <div className="mt-1 text-[12px] text-slate-400">
                    {order.dropPhone || order.customerSnapshot?.phone || "-"}
                  </div>
                </td>
                <td className="px-4 py-3">{fmtDateTime(order.createdAt)}</td>
                <td className="px-4 py-3">{fmtDateTime(order.deliveredAt)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-[14px] text-slate-400">
                No delivery order data found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Card className="mt-5 p-5 sm:p-6">
        <div className="mb-4 flex items-center gap-2 text-[18px] font-semibold text-slate-700">
          <ShoppingBag size={18} color={PINK} />
          Delivery Orders
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <OrderStatCard label="Total Orders" value={summary.totalOrders} hint="All assigned orders" />
          <OrderStatCard label="Active Orders" value={summary.activeOrders} hint="Currently with partner" />
          <OrderStatCard label="Delivered" value={summary.deliveredOrders} hint="Completed deliveries" />
          <OrderStatCard label="Cancelled" value={summary.cancelledOrders} hint="Cancelled / failed tasks" />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Last Order" value={fmtDateTime(summary.lastOrderAt)} />
          <Field label="Last Delivered" value={fmtDateTime(summary.lastDeliveredAt)} />
        </div>

        <div className="mt-5 overflow-hidden rounded-[16px]" style={{ border: `1px solid ${BORDER}` }}>
          <div className="flex flex-wrap items-center justify-between gap-3 bg-[#fff7f8] px-4 py-3">
            <div className="flex items-center gap-2 text-[14px] font-bold text-slate-700">
              <Truck size={16} color={PINK} />
              Recent Orders
            </div>

            {orders.length > 5 ? (
              <button
                type="button"
                onClick={() => setShowAllOrders(true)}
                className="rounded-full border border-[#f2c6cc] bg-white px-4 py-2 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#fff1f3]"
              >
                View All {orders.length} Orders
              </button>
            ) : null}
          </div>

          <div className="overflow-x-auto">
            <OrdersTable rows={recentOrders} />
          </div>
        </div>
      </Card>

      {showAllOrders ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-[1180px] overflow-hidden rounded-[24px] bg-white shadow-2xl">
            <div
              className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4"
              style={{ borderColor: BORDER }}
            >
              <div>
                <h2 className="text-[22px] font-bold text-[#d86a78]">
                  All Delivery Orders
                </h2>
                <p className="mt-1 text-[13px] text-slate-400">
                  {partner?.name || "Partner"} • {filteredOrders.length} orders
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowAllOrders(false)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-rose-50 hover:text-[#d86a78]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              <div className="relative mb-4 max-w-[360px]">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  placeholder="Search order / customer / status"
                  className="h-[44px] w-full rounded-[12px] bg-white pl-10 pr-4 text-[14px] text-slate-600 outline-none"
                  style={{ border: `1px solid ${BORDER}` }}
                />
              </div>

              <div className="overflow-hidden rounded-[16px]" style={{ border: `1px solid ${BORDER}` }}>
                <div className="overflow-x-auto">
                  <OrdersTable rows={filteredOrders} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default function DeliveryPartnerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    vehicleType: "",
    vehicleNumber: "",
    city: "",
    hub: "",
  });

  async function loadProfile() {
    try {
      setLoading(true);
      const res = await DeliveryPartnersService.getById(id);
      const row = res?.partner || null;
      setPartner(row);

      if (row) {
        setForm({
          name: row.name && row.name !== "-" ? row.name : "",
          phone: row.phone && row.phone !== "-" ? row.phone : "",
          vehicleType:
            row.vehicleType && row.vehicleType !== "-" ? row.vehicleType : "",
          vehicleNumber:
            row.vehicleNumber && row.vehicleNumber !== "-"
              ? row.vehicleNumber
              : "",
          city: row.city && row.city !== "-" ? row.city : "",
          hub: row.hub && row.hub !== "-" ? row.hub : "",
        });
        setAvatarPreview(resolveAssetUrl(row.avatar || ""));
      }
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to load partner profile");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProfile();
  }, [id]);

  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleAvatarChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      await DeliveryPartnersService.update(id, {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        vehicleType: form.vehicleType.trim() || null,
        vehicleNumber: form.vehicleNumber.trim() || null,
        city: form.city.trim() || null,
        hub: form.hub.trim() || null,
        avatarFile,
      });

      setAvatarFile(null);
      setIsEditing(false);
      await loadProfile();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to update delivery partner");
    } finally {
      setSaving(false);
    }
  }

  const emergency = useMemo(() => {
    if (!partner?.emergencyContact) return null;
    if (typeof partner.emergencyContact === "string") {
      return { name: partner.emergencyContact, phone: "-", relation: "-" };
    }
    return partner.emergencyContact;
  }, [partner]);

  const address = useMemo(() => {
    if (!partner?.address) return null;
    if (typeof partner.address === "string") {
      return { full: partner.address };
    }
    const a = partner.address;
    return {
      full:
        [
          a.line1 || a.addressLine1 || a.fullAddress,
          a.line2 || a.addressLine2,
          a.city,
          a.state,
          a.pincode || a.pinCode || a.zipcode,
        ]
          .filter(Boolean)
          .join(", ") || "-",
    };
  }, [partner]);

  const documents = useMemo(
    () =>
      [
        { title: "Profile Photo", doc: partner?.profilePhoto },
        { title: "Aadhaar Document", doc: partner?.aadhaarDoc },
        { title: "Driving License", doc: partner?.dlDoc },
        { title: "Bank Passbook", doc: partner?.bankPassbookDoc },
        { title: "Bike RC", doc: partner?.bikeRcDoc },
      ].filter((item) => item.doc && getDocUrl(item.doc)),
    [partner]
  );

  if (loading) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center text-slate-400">
          Loading delivery partner profile...
        </Card>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center text-slate-400">
          Delivery partner not found.
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-white overflow-x-hidden">
      <div className="w-full px-3 py-4 sm:px-4 md:px-5 lg:px-6 xl:px-6 2xl:px-8">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard/delivery-partners")}
              className="mb-3 inline-flex items-center gap-2 text-[14px] font-semibold text-slate-500 transition hover:text-[#d86a78]"
            >
              <ArrowLeft size={16} />
              Back to Delivery Partners
            </button>

            <h1 className="text-[24px] sm:text-[30px] lg:text-[34px] font-bold leading-tight tracking-wide text-[#d86a78]">
              DELIVERY PARTNER PROFILE
            </h1>
            {/* <p className="mt-2 text-[14px] text-slate-400">
              All partner details and uploaded documents.
            </p> */}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={loadProfile}
              className="inline-flex h-[44px] items-center justify-center rounded-full px-5 text-[14px] font-semibold text-slate-600 transition hover:bg-slate-50"
              style={{ border: `1px solid ${BORDER}` }}
            >
              <RefreshCcw size={16} className="mr-2" />
              Refresh
            </button>

            <button
              type="button"
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                  setAvatarFile(null);
                  setAvatarPreview(resolveAssetUrl(partner.avatar || ""));
                  setForm({
                    name: partner.name && partner.name !== "-" ? partner.name : "",
                    phone:
                      partner.phone && partner.phone !== "-" ? partner.phone : "",
                    vehicleType:
                      partner.vehicleType && partner.vehicleType !== "-"
                        ? partner.vehicleType
                        : "",
                    vehicleNumber:
                      partner.vehicleNumber && partner.vehicleNumber !== "-"
                        ? partner.vehicleNumber
                        : "",
                    city: partner.city && partner.city !== "-" ? partner.city : "",
                    hub: partner.hub && partner.hub !== "-" ? partner.hub : "",
                  });
                } else {
                  setIsEditing(true);
                }
              }}
              className="inline-flex h-[44px] items-center justify-center rounded-full px-5 text-[14px] font-semibold text-white transition"
              style={{ backgroundColor: PINK }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = PINK_HOVER)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = PINK)
              }
            >
              <Pencil size={16} className="mr-2" />
              {isEditing ? "Cancel Edit" : "Edit Partner"}
            </button>
          </div>
        </div>

        <Card className="mt-5 p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
            <div
              className="flex flex-col items-center rounded-[18px] bg-[#fffafb] p-5"
              style={{ border: `1px solid ${BORDER}` }}
            >
              <div
                className="flex h-[130px] w-[130px] items-center justify-center overflow-hidden rounded-full bg-slate-50"
                style={{ border: `1px solid ${BORDER}` }}
              >
                {avatarPreview || partner.avatar ? (
                  <img
                    src={avatarPreview || resolveAssetUrl(partner.avatar)}
                    alt={partner.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User size={44} className="text-slate-300" />
                )}
              </div>

              <div className="mt-4 text-center">
                <div className="text-[22px] font-bold text-slate-700">
                  {partner.name}
                </div>
                <div className="mt-1 text-[13px] text-slate-400">
                  {partner.partnerId || "-"}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <Badge type={partner.status === "ACTIVE" ? "active" : "inactive"}>
                  {partner.status === "ACTIVE" ? "Active" : "Inactive"}
                </Badge>

                <Badge
                  type={
                    partner.approvalStatus === "APPROVED"
                      ? "approved"
                      : partner.approvalStatus === "REJECTED"
                        ? "rejected"
                        : "pending"
                  }
                >
                  {partner.approvalStatus}
                </Badge>
              </div>

              {isEditing ? (
                <div className="mt-5 w-full">
                  <div className="mb-2 text-[13px] font-semibold text-slate-600">
                    Profile Picture
                  </div>

                  <div
                    className="rounded-[14px] border p-4"
                    style={{ borderColor: BORDER }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-rose-50 file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-[#d86a78] hover:file:bg-rose-100"
                    />
                    <div className="mt-2 text-xs text-slate-500">
                      {avatarFile ? "Image selected" : "Choose image"}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              {isEditing ? (
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <InputField
                      label="Full Name"
                      name="name"
                      value={form.name}
                      onChange={handleFormChange}
                    />
                    <Field label="Email" value={partner.email} />
                    <InputField
                      label="Phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleFormChange}
                    />
                    <InputField
                      label="Vehicle Type"
                      name="vehicleType"
                      value={form.vehicleType}
                      onChange={handleFormChange}
                    />
                    <InputField
                      label="Vehicle Number"
                      name="vehicleNumber"
                      value={form.vehicleNumber}
                      onChange={handleFormChange}
                    />
                    <InputField
                      label="City"
                      name="city"
                      value={form.city}
                      onChange={handleFormChange}
                    />
                    <InputField
                      label="Hub"
                      name="hub"
                      value={form.hub}
                      onChange={handleFormChange}
                    />
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex h-[46px] items-center justify-center rounded-full px-6 text-[14px] font-semibold text-white transition disabled:opacity-60"
                      style={{ backgroundColor: PINK }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = PINK_HOVER)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = PINK)
                      }
                    >
                      {saving ? "Updating..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <Field label="Email" value={partner.email} />
                  <Field label="Phone" value={partner.phone} />
                  <Field label="Vehicle Type" value={partner.vehicleType} />
                  <Field label="Vehicle Number" value={partner.vehicleNumber} />
                  <Field label="City" value={partner.city} />
                  <Field label="Hub" value={partner.hub} />
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
          <Card className="p-5 sm:p-6">
            <div className="mb-4 flex items-center gap-2 text-[18px] font-semibold text-slate-700">
              <Phone size={18} color={PINK} />
              Emergency & Address
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label="Emergency Name" value={emergency?.name || "-"} />
              <Field label="Emergency Phone" value={emergency?.phone || "-"} />
              <Field
                label="Emergency Relation"
                value={emergency?.relation || "-"}
              />
              <div className="md:col-span-2">
                <Field label="Address" value={address?.full || "-"} />
              </div>
            </div>
          </Card>
        </div>

        <DeliveryOrderInfoCard partner={partner} />

        <Card className="mt-5 p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2 text-[18px] font-semibold text-slate-700">
            <CheckCircle2 size={18} color={PINK} />
            Onboarding Details
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            <Field label="Father Name" value={partner.fatherName} />
            <Field label="Mother Name" value={partner.motherName} />
            <Field label="Aadhaar Number" value={partner.aadhaarNumber} />
            <Field label="PAN Number" value={partner.panNumber} />
            <Field label="RC Number" value={partner.rcNumber} />
            <Field label="DL Number" value={partner.dlNumber} />
            <Field label="Bank Account" value={partner.bankAccount} />
            <Field label="Bank IFSC" value={partner.bankIfsc} />
            <Field label="Bank Holder Name" value={partner.bankHolderName} />
          </div>
        </Card>

        <Card className="mt-5 p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2 text-[18px] font-semibold text-slate-700">
            <FileText size={18} color={PINK} />
            Uploaded Documents
          </div>

          {documents.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {documents.map((item) => (
                <DocumentCard
                  key={item.title}
                  title={item.title}
                  doc={item.doc}
                />
              ))}
            </div>
          ) : (
            <div className="text-[14px] text-slate-400">
              No documents uploaded.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
